import { ActionListProvider } from 'ActionListContext';
import { CommandLayout } from 'components/CommandLayout';
import { DefaultOptionComponent } from 'components/DefaultOptionComponent';
import { PopoverLayout } from 'components/PopoverLayout';
import { noop } from 'lodash-es';
import type { RefObject } from 'react';
import { useCallback, useMemo, useState } from 'react';
import type { ActionListContextProps, ActionListProps } from 'types';

export const ActionList = <
  isMulti extends boolean = false,
  isNullable extends boolean = false,
  T = undefined,
>({
  onChange,
  disabled,
  children,
  closeOnOptionClick,
  placeholder = 'Search...',
  withSearch,
  open: controlledOpen,
  isMulti,
  isNullable,
  allowSelectAll,
  selectAllLabel,
  setOpen: controlledSetOpen,
  onOpenChange,
  options,
  OptionComponent = DefaultOptionComponent,
  showSelectionElement,
  popoverProps,
  popoverContentProps,
  itemSize,
  ref,
  clearSearchOnClose = true,
  defaultOpen = false,
  popoverClassname = '',
  loading = false,
  rawProps = {},
  value,
  highlightSelectedItem,
  'data-testid': testID,
  onScroll = noop,
  isExternalSearch = false,
  listRef,
  type = 'withPopover',
  noResultsText = 'No results found.',
  onSearchChange: passedOnSearchChange = noop,
  inlineLoading,
  defaultSearchTerm,
  searchTerm: controlledSearchTerm,
  searchFn,
  selectionElementPosition = 'left',
  showSelectedValue,
  truncateOptions = false,
  searchByLabelOnly = false,
  hideScrollbar = true,
  maxCharacters,
  characterLimitReachedLabel,
  onDismiss,
  error,
  errorMessage,
}: ActionListProps<isMulti, isNullable, T> & {
  ref?: RefObject<HTMLInputElement | null>;
}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const [internalSearchTerm, setInternalSearchTerm] = useState<string>(
    defaultSearchTerm || '',
  );
  const resolvedSearchTerm =
    controlledSearchTerm !== undefined
      ? controlledSearchTerm
      : internalSearchTerm;

  const shouldCloseOnClick = useMemo(
    () =>
      closeOnOptionClick !== undefined
        ? closeOnOptionClick
        : isMulti
          ? false
          : true,
    [closeOnOptionClick, isMulti],
  );

  const [open, setOpen] = useMemo(() => {
    if (controlledOpen !== undefined && controlledSetOpen !== undefined) {
      return [controlledOpen, controlledSetOpen];
    }

    return [uncontrolledOpen, setUncontrolledOpen];
  }, [
    controlledOpen,
    controlledSetOpen,
    uncontrolledOpen,
    setUncontrolledOpen,
  ]);

  const handleOpenOrClose = useCallback(
    (isOpen: boolean) => {
      if (open === isOpen) {
        return;
      }

      setOpen(isOpen);

      if (isOpen) {
        return;
      }

      onDismiss?.();

      // Guard on `resolvedSearchTerm !== ''` to preserve "fire only on actual
      // change" semantics: the original useEffect on [searchTerm] no-op'd
      // when setInternalSearchTerm('') was called against an already-empty
      // value (React bails out of identical setState). Without this guard
      // we'd fire a spurious '' notification on every close even when
      // nothing changed.
      if (clearSearchOnClose && resolvedSearchTerm !== '') {
        setInternalSearchTerm('');
        passedOnSearchChange('');
      }

      // Bug fix: Radix UI leaves pointer-events none on body when dialog is closed
      // https://github.com/radix-ui/primitives/issues/1241#issuecomment-2048551011
      // We also want to delay clearing search so it doesn't shift while closing
      setTimeout(() => {
        document.body.style.pointerEvents = 'auto';
      }, 100);
    },
    [
      open,
      setOpen,
      onDismiss,
      clearSearchOnClose,
      passedOnSearchChange,
      resolvedSearchTerm,
    ],
  );

  const onSearchTermChange = useCallback(
    (searchTerm: string) => {
      setInternalSearchTerm(searchTerm);
      // Notify the consumer of the search-term change. Done here (rather
      // than from a useEffect on [searchTerm]) so we don't also fire on
      // mount with the initial value — LTDS-48.
      passedOnSearchChange(searchTerm);
    },
    [passedOnSearchChange],
  );

  const handleProviderChange: ActionListContextProps<
    isMulti,
    isNullable,
    T
  >['onChange'] = useCallback(
    (newVal, fullValue) => {
      onChange(newVal, fullValue);

      if (shouldCloseOnClick) {
        handleOpenOrClose(false);
      }
    },
    [onChange, shouldCloseOnClick, handleOpenOrClose],
  );

  return (
    <ActionListProvider<isMulti, isNullable, T>
      isMulti={isMulti}
      onChange={handleProviderChange}
      isNullable={isNullable}
      allowSelectAll={allowSelectAll}
      options={options}
      value={value}
      OptionComponent={OptionComponent}
      searchTerm={resolvedSearchTerm}
      isExternalSearch={isExternalSearch}
      inlineLoading={inlineLoading}
      searchFn={searchFn}
      selectionElementPosition={selectionElementPosition}
      showSelectedValue={showSelectedValue}
      searchByLabelOnly={searchByLabelOnly}
    >
      {type === 'withPopover' && (
        <PopoverLayout
          disabled={disabled}
          defaultOpen={defaultOpen}
          onOpenChange={onOpenChange}
          popoverProps={popoverProps}
          popoverContentProps={popoverContentProps}
          data-testid={testID}
          popoverClassname={popoverClassname}
          popoverTrigger={children}
          open={open}
          handleOpenOrClose={handleOpenOrClose}
          hideScrollbar={hideScrollbar}
        >
          <CommandLayout
            {...rawProps}
            disabled={disabled}
            loading={loading}
            isMulti={isMulti}
            withSearch={withSearch}
            placeholder={placeholder}
            itemSize={itemSize}
            showSelectionElement={showSelectionElement}
            noResultsText={noResultsText}
            rawProps={rawProps}
            searchTerm={resolvedSearchTerm}
            onSearchTermChange={onSearchTermChange}
            onScroll={onScroll}
            listRef={listRef}
            truncateOptions={truncateOptions}
            selectAllLabel={selectAllLabel}
            hideScrollbar={hideScrollbar}
            maxCharacters={maxCharacters}
            characterLimitReachedLabel={characterLimitReachedLabel}
            error={error}
            errorMessage={errorMessage}
            highlightSelectedItem={highlightSelectedItem}
          />
        </PopoverLayout>
      )}

      {type === 'withoutPopover' && (
        <CommandLayout
          {...rawProps}
          ref={ref}
          disabled={disabled}
          loading={loading}
          isMulti={isMulti}
          withSearch={withSearch}
          placeholder={placeholder}
          itemSize={itemSize}
          showSelectionElement={showSelectionElement}
          noResultsText={noResultsText}
          rawProps={rawProps}
          searchTerm={resolvedSearchTerm}
          onSearchTermChange={onSearchTermChange}
          onScroll={onScroll}
          listRef={listRef}
          maxCharacters={maxCharacters}
          characterLimitReachedLabel={characterLimitReachedLabel}
          withoutPopover
          truncateOptions={truncateOptions}
          selectAllLabel={selectAllLabel}
          hideScrollbar={hideScrollbar}
          error={error}
          errorMessage={errorMessage}
          highlightSelectedItem={highlightSelectedItem}
        />
      )}
    </ActionListProvider>
  );
};
